
import { 
  Code, 
  Database, 
  Cloud, 
  Shield, 
  Smartphone, 
  Monitor, 
  Server, 
  Network,
  Cpu,
  HardDrive,
  Wifi,
  Lock,
  Terminal,
  GitBranch,
  Bot,
  Brain,
  Layers,
  Globe,
  Zap,
  Settings,
  Package,
  Webhook
} from 'lucide-react';
import type { Product } from './types';

export const technologyProducts: Product[] = [
  {
    id: 'cloud-infrastructure-management',
    title: 'Cloud Infrastructure Management',
    subtitle: 'Multi-cloud operations & automation platform',
    description: 'Enterprise-grade cloud infrastructure management with AI-powered optimization, multi-cloud orchestration, cost intelligence, and advanced security. Outperforming AWS Control Tower, Azure Arc, and Google Cloud Operations.',
    icon: Cloud,
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#cloud-services', '#devops', '#technology', '#startups'],
    features: [
      'AI-Powered Multi-Cloud Orchestration',
      'Predictive Cost Optimization & Analytics',
      'Automated Scaling & Resource Management',
      'Zero-Trust Security & Compliance',
      'Infrastructure as Code (IaC) Automation',
      'Advanced Performance Monitoring',
      'Disaster Recovery & Business Continuity',
      'DevOps Integration & CI/CD Pipeline',
      'Global Edge Computing Support',
      'Comprehensive API Ecosystem'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'cybersecurity-management-suite',
    title: 'Cybersecurity Management Suite',
    subtitle: 'AI-powered enterprise security platform',
    description: 'Comprehensive cybersecurity platform with advanced threat detection, AI-powered response automation, and enterprise compliance. Superior to CrowdStrike, SentinelOne, and Palo Alto Networks.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563206767-5b18f218e8de?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#cybersecurity', '#compliance', '#financial-services', '#government'],
    features: [
      'AI-Powered Threat Detection & Response',
      'Advanced Security Information & Event Management',
      'Automated Incident Response & Forensics',
      'Zero-Trust Identity & Access Management',
      'Behavioral Analytics & Threat Hunting',
      'Multi-Vector Protection & Defense',
      'Compliance Automation (GDPR, SOC 2, ISO)',
      'Security Awareness Training Platform',
      'Threat Intelligence & Analytics',
      'Executive Security Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-red-500 to-pink-600'
  },
  {
    id: 'database-management-system',
    title: 'Database Management System',
    subtitle: 'AI-optimized database platform',
    description: 'Advanced database management with AI-powered optimization, multi-database support, automated scaling, and enterprise security. Outperforming Oracle Database, Microsoft SQL Server, and MongoDB Atlas.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#database-management', '#data-analytics', '#technology', '#saas'],
    features: [
      'AI-Powered Performance Optimization',
      'Multi-Database Type Management',
      'Automated Backup & Disaster Recovery',
      'Zero-Downtime Migration Tools',
      'Advanced Security & Encryption',
      'Real-Time Monitoring & Analytics',
      'Predictive Capacity Planning',
      'DevOps Pipeline Integration',
      'Global Data Replication',
      'Comprehensive API Ecosystem'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'mobile-app-development-platform',
    title: 'Mobile App Development Platform',
    subtitle: 'AI-powered cross-platform development',
    description: 'Revolutionary mobile development platform with AI-assisted coding, visual development tools, and automated testing. Superior to React Native, Flutter, and Xamarin development environments.',
    icon: Smartphone,
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#mobile-development', '#app-agencies', '#startups', '#enterprise', '#saas'],
    features: [
      'AI-Powered Development Assistant',
      'Cross-Platform Native Compilation',
      'Visual Drag-and-Drop Interface Builder',
      'Automated Testing & Quality Assurance',
      'Backend Integration & API Management',
      'App Store Optimization & Deployment',
      'Real-Time Analytics & Performance Monitoring',
      'Collaborative Development Environment',
      'Advanced Integration Marketplace',
      'Performance Optimization Tools'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'enterprise-api-management',
    title: 'Enterprise API Management',
    subtitle: 'Comprehensive API lifecycle platform',
    description: 'Complete API management platform with advanced security, analytics, and developer experience. Outperforming MuleSoft, Apigee, and Kong API management solutions.',
    icon: Network,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#api-management', '#integration', '#developers', '#saas'],
    features: [
      'Complete API Lifecycle Management',
      'Advanced Security & Access Control',
      'Real-Time Analytics & Monitoring',
      'Developer Portal & Documentation',
      'API Gateway & Rate Limiting',
      'Microservices Architecture Support',
      'GraphQL & REST API Support',
      'Automated Testing & Validation',
      'Enterprise Integration Patterns',
      'Multi-Cloud Deployment'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-cyan-600'
  },
  {
    id: 'devops-automation-platform',
    title: 'DevOps Automation Platform',
    subtitle: 'AI-powered CI/CD & deployment automation',
    description: 'Comprehensive DevOps platform with AI-assisted automation, advanced pipeline management, and enterprise security. Superior to Jenkins, GitLab CI/CD, and Azure DevOps.',
    icon: GitBranch,
    image: 'https://images.unsplash.com/photo-1556075798-4825dfaaf498?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#devops', '#software-development', '#enterprise', '#startups', '#technology'],
    features: [
      'AI-Powered Pipeline Optimization',
      'Advanced CI/CD Automation',
      'Infrastructure as Code Management',
      'Container Orchestration & Kubernetes',
      'Automated Testing & Quality Gates',
      'Security Scanning & Compliance',
      'Multi-Cloud Deployment Support',
      'Real-Time Monitoring & Alerting',
      'Collaborative Development Workflows',
      'Enterprise Integration Ecosystem'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'ai-development-platform',
    title: 'AI Development Platform',
    subtitle: 'Complete machine learning & AI workspace',
    description: 'Enterprise AI development platform with automated model training, deployment, and monitoring. Outperforming AWS SageMaker, Google AI Platform, and Azure Machine Learning.',
    icon: Bot,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#ai-development', '#data-science', '#enterprise', '#research', '#technology'],
    features: [
      'Automated Model Training & Optimization',
      'No-Code AI Model Builder',
      'Advanced Data Processing Pipeline',
      'Real-Time Model Deployment',
      'Comprehensive Model Monitoring',
      'AI Ethics & Bias Detection',
      'Collaborative AI Development',
      'Pre-Built Industry Models',
      'Advanced Analytics & Insights',
      'Enterprise Security & Compliance'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-600'
  },
  {
    id: 'enterprise-monitoring-platform',
    title: 'Enterprise Monitoring Platform',
    subtitle: 'AI-powered observability & performance',
    description: 'Comprehensive monitoring and observability platform with AI-powered insights, predictive analytics, and automated remediation. Superior to Datadog, New Relic, and Splunk.',
    icon: Monitor,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#monitoring', '#observability', '#devops', '#saas'],
    features: [
      'AI-Powered Anomaly Detection',
      'Comprehensive Infrastructure Monitoring',
      'Application Performance Management',
      'Real-Time Log Analysis & Aggregation',
      'Distributed Tracing & Debugging',
      'Predictive Performance Analytics',
      'Automated Incident Response',
      'Custom Dashboard & Visualization',
      'Advanced Alert Management',
      'Enterprise Compliance Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-emerald-500 to-green-600'
  },
  {
    id: 'enterprise-backup-recovery',
    title: 'Enterprise Backup & Recovery',
    subtitle: 'AI-optimized data protection platform',
    description: 'Advanced backup and disaster recovery platform with AI-powered optimization, ransomware protection, and global replication. Outperforming Veeam, Commvault, and Rubrik.',
    icon: HardDrive,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#data-protection', '#disaster-recovery', '#compliance', '#healthcare'],
    features: [
      'AI-Powered Backup Optimization',
      'Ransomware Detection & Protection',
      'Cross-Platform Data Protection',
      'Instant Recovery & Failover',
      'Global Data Replication',
      'Compliance & Retention Management',
      'Advanced Encryption & Security',
      'Automated Testing & Validation',
      'Cloud-Native Architecture',
      'Comprehensive Reporting & Analytics'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-blue-600'
  },
  {
    id: 'network-security-platform',
    title: 'Network Security Platform',
    subtitle: 'Next-generation firewall & threat protection',
    description: 'Advanced network security platform with AI-powered threat detection, zero-trust architecture, and automated response. Superior to Palo Alto Networks, Fortinet, and Check Point.',
    icon: Wifi,
    image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#network-security', '#cybersecurity', '#financial-services', '#government'],
    features: [
      'Next-Generation Firewall (NGFW)',
      'AI-Powered Threat Detection',
      'Zero-Trust Network Architecture',
      'Advanced Intrusion Prevention',
      'SD-WAN & Network Optimization',
      'Automated Incident Response',
      'VPN & Remote Access Security',
      'Network Segmentation & Microsegmentation',
      'Comprehensive Traffic Analysis',
      'Enterprise Compliance Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-600'
  },
  {
    id: 'enterprise-virtualization-platform',
    title: 'Enterprise Virtualization Platform',
    subtitle: 'AI-optimized virtual infrastructure',
    description: 'Advanced virtualization platform with AI-powered resource optimization, automated management, and hybrid cloud integration. Outperforming VMware vSphere, Hyper-V, and Citrix.',
    icon: Server,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#virtualization', '#cloud-services', '#data-centers', '#technology'],
    features: [
      'AI-Powered Resource Optimization',
      'Automated VM Lifecycle Management',
      'High Availability & Fault Tolerance',
      'Live Migration & Load Balancing',
      'Hybrid Cloud Integration',
      'Advanced Security & Isolation',
      'Performance Monitoring & Analytics',
      'Disaster Recovery & Backup',
      'Container & Kubernetes Support',
      'Enterprise Management Console'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'identity-access-management',
    title: 'Identity & Access Management',
    subtitle: 'Zero-trust identity security platform',
    description: 'Comprehensive identity and access management platform with zero-trust architecture, AI-powered risk assessment, and seamless integration. Superior to Okta, Ping Identity, and Microsoft Azure AD.',
    icon: Lock,
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#identity-management', '#security', '#compliance', '#saas'],
    features: [
      'Zero-Trust Identity Architecture',
      'AI-Powered Risk Assessment',
      'Single Sign-On (SSO) & Federation',
      'Multi-Factor Authentication (MFA)',
      'Privileged Access Management',
      'Identity Governance & Administration',
      'Automated Provisioning & Deprovisioning',
      'Comprehensive Audit & Compliance',
      'API Security & Protection',
      'Advanced Threat Protection'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-amber-500 to-orange-600'
  },
  {
    id: 'container-orchestration-platform',
    title: 'Container Orchestration Platform',
    subtitle: 'Enterprise Kubernetes & container management',
    description: 'Advanced container orchestration platform with AI-powered optimization, automated scaling, and enterprise security. Superior to Red Hat OpenShift, Rancher, and Docker Enterprise.',
    icon: Package,
    image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#containers', '#kubernetes', '#microservices', '#devops'],
    features: [
      'AI-Powered Container Optimization',
      'Advanced Kubernetes Management',
      'Automated Scaling & Load Balancing',
      'Multi-Cloud Container Deployment',
      'Enterprise Security & Compliance',
      'Service Mesh & Microservices',
      'CI/CD Pipeline Integration',
      'Comprehensive Monitoring & Logging',
      'Disaster Recovery & Backup',
      'Developer Experience Platform'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-green-600'
  },
  {
    id: 'enterprise-integration-platform',
    title: 'Enterprise Integration Platform',
    subtitle: 'AI-powered integration & automation hub',
    description: 'Comprehensive integration platform with AI-powered data transformation, real-time processing, and enterprise connectivity. Outperforming MuleSoft, Boomi, and Informatica.',
    icon: Webhook,
    image: 'https://images.unsplash.com/photo-1551808525-51a94da548ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#integration', '#automation', '#data-transformation', '#saas'],
    features: [
      'AI-Powered Data Transformation',
      'Real-Time Integration Processing',
      'Enterprise Application Connectivity',
      'Advanced Workflow Automation',
      'API Management & Governance',
      'Event-Driven Architecture',
      'Hybrid Cloud Integration',
      'Comprehensive Monitoring & Analytics',
      'Pre-Built Industry Connectors',
      'Enterprise Security & Compliance'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-600'
  },
  {
    id: 'performance-testing-platform',
    title: 'Performance Testing Platform',
    subtitle: 'AI-driven load testing & optimization',
    description: 'Advanced performance testing platform with AI-powered test generation, real-time analytics, and automated optimization. Superior to LoadRunner, JMeter, and BlazeMeter.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#performance-testing', '#quality-assurance', '#devops', '#saas'],
    features: [
      'AI-Powered Test Generation',
      'Realistic Load Simulation',
      'Real-Time Performance Analytics',
      'Automated Bottleneck Detection',
      'Global Load Testing Infrastructure',
      'API & Protocol Testing',
      'CI/CD Pipeline Integration',
      'Advanced Reporting & Insights',
      'Performance Optimization Recommendations',
      'Enterprise Scalability Testing'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-600'
  },
  {
    id: 'enterprise-content-management',
    title: 'Enterprise Content Management',
    subtitle: 'AI-powered document & knowledge platform',
    description: 'Comprehensive content management platform with AI-powered search, automated workflows, and enterprise collaboration. Outperforming SharePoint, Box, and Documentum.',
    icon: Settings,
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Technology Products',
    status: 'Available',
    businessTypes: ['#enterprise', '#content-management', '#collaboration', '#knowledge-management', '#compliance'],
    features: [
      'AI-Powered Content Intelligence',
      'Advanced Document Management',
      'Automated Workflow Processing',
      'Enterprise Search & Discovery',
      'Collaboration & Version Control',
      'Records Management & Compliance',
      'Digital Asset Management',
      'Advanced Security & Permissions',
      'Integration & API Connectivity',
      'Analytics & Usage Insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-indigo-600'
  }
];
